import { getActions } from '../global';

import { DEBUG, DEBUG_MORE, IS_TEST } from '../config';
import { IS_ANDROID, IS_IOS, IS_SERVICE_WORKER_SUPPORTED } from './browser/windowEnvironment';
import { isCrmIframeMode } from './crmBridge';
import { formatShareText } from './deeplink';
import { validateFiles } from './files';
import { notifyClientReady, playNotifySoundDebounced } from './notifications';

// In CRM iframe mode we deliberately skip the Service Worker:
//
//  1. We don't need its caching layer — tt is served from tgcrm.app which
//     already has nginx caching + Cloudflare. Adding a SW cache on top
//     introduces stale-asset bugs (we just hit one) without speed gain.
//  2. We don't need push notifications — the CRM is operator-facing in
//     a tab; notifications would compete with the operator's own browser
//     notifications and fire from the real browser context (not via our
//     proxy-relay).
//  3. Most importantly: SW handles 'fetch' events for ANY origin via
//     respondWithCacheNetworkFirst, including upstream Telegram CDN.
//     Those fetches go from the browser process (real IP), not through
//     our proxy-relay — yet another IP-leak surface we close by simply
//     not running the SW.
const SKIP_SW_IN_CRM_IFRAME = isCrmIframeMode();
if (SKIP_SW_IN_CRM_IFRAME && 'serviceWorker' in navigator) {
  // Best-effort: actively unregister anything left over from a prior
  // visit so it doesn't keep intercepting fetches.
  navigator.serviceWorker.getRegistrations().then((regs) => {
    if (!regs.length) return;
    // eslint-disable-next-line no-console
    console.info(`[CRM bridge] unregistering ${regs.length} stale service worker(s) (iframe mode skips SW)`);
    regs.forEach((r) => r.unregister());
  }).catch(() => { /* ignore */ });
}

type WorkerAction = {
  type: string;
  payload: Record<string, any>;
};

const IGNORE_WORKER_PATH = '/k/';

function handleWorkerMessage(e: MessageEvent) {
  const action: WorkerAction = e.data;
  if (DEBUG_MORE) {
    // eslint-disable-next-line no-console
    console.log('[SW] Message from worker', action);
  }
  if (!action.type) return;
  const dispatch = getActions();
  const payload = action.payload;
  switch (action.type) {
    case 'focusMessage':
      dispatch.focusMessage?.(payload as any);
      break;
    case 'playNotificationSound':
      playNotifySoundDebounced(action.payload.id);
      break;
    case 'share':
      dispatch.openChatWithDraft({
        text: formatShareText(payload.url, payload.text, payload.title),
        files: validateFiles(payload.files),
      });
      break;
  }
}

function subscribeToWorker() {
  navigator.serviceWorker.removeEventListener('message', handleWorkerMessage);
  navigator.serviceWorker.addEventListener('message', handleWorkerMessage);
  // Notify web worker that client is ready to receive messages
  notifyClientReady();
}

if (IS_SERVICE_WORKER_SUPPORTED && !SKIP_SW_IN_CRM_IFRAME) {
  window.addEventListener('load', async () => {
    try {
      const controller = navigator.serviceWorker.controller;
      if (!controller || controller.scriptURL.includes(IGNORE_WORKER_PATH)) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const ourRegistrations = registrations.filter((r) => !r.scope.includes(IGNORE_WORKER_PATH));
        if (ourRegistrations.length) {
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[SW] Hard reload detected, re-enabling Service Worker');
          }
          await Promise.all(ourRegistrations.map((r) => r.unregister()));
        }
      }

      await navigator.serviceWorker.register(new URL('../serviceWorker', import.meta.url));

      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[SW] ServiceWorker registered');
      }

      await navigator.serviceWorker.ready;

      // Wait for registration to be available
      await navigator.serviceWorker.getRegistration();

      if (navigator.serviceWorker.controller) {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[SW] ServiceWorker ready');
        }
        subscribeToWorker();
      } else {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.error('[SW] ServiceWorker not available');
        }

        if (!IS_IOS && !IS_ANDROID && !IS_TEST) {
          getActions().showDialog?.({ data: { type: 'error', message: 'SERVICE_WORKER_DISABLED', hasErrorKey: true } });
        }
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.error('[SW] ServiceWorker registration failed: ', err);
      }
    }
  });
  window.addEventListener('focus', async () => {
    await navigator.serviceWorker.ready;
    subscribeToWorker();
  });
}
